import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import type { Breakpoint } from '@mui/system';

import Headline from '../Headline';
import ValueIndicator from '../ValueIndicator';
import FormComponent from '../FormComponent';
import { FormOperation } from './useFormDialog';
import { riskAssessmentMapping } from '../../constants';
import type { Form } from '../../types/form';
import Link from '../Link';

type Props<T> = {
  isOpen: boolean;
  onClose: () => void;
  item: T;
  formOperation: FormOperation;
  form: Form<T>;
  maxWidth?: Breakpoint;
  handleChange: (item: T) => void;
  saveClient?: (item: T) => void;
};

type Validation = {
  [key: string]: string;
};

function validate<T>(item: T, formFieldKeys: string[]): Validation {
  return formFieldKeys.reduce((errorMessages, formFieldKey) => {
    const value = item[formFieldKey as keyof T];
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmptyString = typeof value === 'string' && value.trim().length === 0;

    if (value === undefined || value === null || isEmptyArray || isEmptyString) {
      return {
        ...errorMessages,
        [formFieldKey]: `Bitte geben Sie einen Wert für das Feld ein.`,
      };
    }

    return errorMessages;
  }, {});
}

function FormDialog<T>(props: Props<T>) {
  const { isOpen, onClose, formOperation, form, handleChange, saveClient } = props;
  const [item, setItem] = React.useState<T>(props.item);
  const [touchedFieldKeys, setTouchedFieldKeys] = React.useState<string[]>([]);
  const formFieldsToValidate = form.fields
    .filter(
      (field) =>
        field.fieldTypes !== 'headline' &&
        field.fieldTypes !== 'valueIndicator' &&
        field.fieldTypes !== 'link' &&
        field.validateIsNotEmpty,
    )
    .map((field) => field.key);

  const maxWidth = props.maxWidth != null ? props.maxWidth : 'sm';

  const operationLabel = formOperation === FormOperation.CREATE ? 'Hinzufügen' : 'Bearbeiten';

  const addTouchedFieldKey = (fieldKey: string) => {
    const isAlreadyTouched = touchedFieldKeys.includes(fieldKey);
    if (!isAlreadyTouched) {
      setTouchedFieldKeys(touchedFieldKeys.concat([fieldKey]));
    }
  };

  const errorMessages = validate(item, formFieldsToValidate);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle>
        {form.title} {operationLabel.toLowerCase()}
      </DialogTitle>
      <DialogContent>
        <Grid container rowSpacing={2} columnSpacing={4}>
          {form.fields.map((field) => {
            if (field.fieldTypes === 'headline') {
              return <Headline field={field} />;
            }
            if (field.fieldTypes === 'valueIndicator') {
              return <ValueIndicator field={field} item={item} iconSortMapping={riskAssessmentMapping} />;
            }

            if (field.fieldTypes === 'link') {
              return <Link field={field} item={item} />;
            }
            const isFieldTouched = touchedFieldKeys.includes(field.key);
            const isFieldVisible = field.isVisible != null ? field.isVisible(item) : true;
            if (!isFieldVisible) {
              return null;
            }

            return (
              <Grid key={field.key} item xs={field.xsWidth != null ? field.xsWidth : 12}>
                <FormComponent
                  key={field.key}
                  fullWidth
                  field={field}
                  error={isFieldTouched && Boolean(errorMessages[field.key])}
                  helperText={isFieldTouched ? errorMessages[field.key] : null}
                  handleChange={(value) => {
                    const updatedItem = { ...item, [field.key]: value };
                    setItem(updatedItem);
                    addTouchedFieldKey(field.key);
                  }}
                  handleBlur={() => {
                    addTouchedFieldKey(field.key);
                  }}
                  value={item[field.key] || ''}
                />
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={() => {
            setTouchedFieldKeys(formFieldsToValidate);
            if (Object.keys(errorMessages).length === 0) {
              handleChange(item);
              if (saveClient !== null && saveClient !== undefined) {
                saveClient(item);
              }
              onClose();
            }
          }}
        >
          {operationLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FormDialog;
